
import React, { useState, useEffect } from 'react';
import { useAuthPayment } from '@/contexts/AuthPaymentContext';
import { AuthPaymentCard } from '@/components/courier/AuthPaymentCard';
import { toast } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';

interface AuthPaymentBlockerProps {
  children: React.ReactNode;
  bypassCheck?: boolean; // Новый параметр для обхода проверки
}

export const AuthPaymentBlocker: React.FC<AuthPaymentBlockerProps> = ({ 
  children, 
  bypassCheck = false 
}) => {
  const { authPayment, isAuthPaymentLoading, refreshAuthPayment } = useAuthPayment();
  const [isAuthorizingPayment, setIsAuthorizingPayment] = useState<boolean>(false);
  const [forceShowContent, setForceShowContent] = useState<boolean>(false);
  
  // Принудительно завершаем загрузку через 10 секунд для предотвращения зависания
  useEffect(() => {
    if (isAuthPaymentLoading) {
      const timeout = setTimeout(() => {
        console.warn('AuthPaymentBlocker: Принудительное завершение загрузки через timeout');
        setForceShowContent(true);
      }, 10000); // 10 секунд
      
      return () => clearTimeout(timeout);
    } else {
      setForceShowContent(false);
    }
  }, [isAuthPaymentLoading]);

  // Добавляем отладочную информацию
  console.log('AuthPaymentBlocker:', {
    isAuthPaymentLoading,
    hasAuthPayment: !!authPayment,
    bypassCheck,
    forceShowContent
  });
  
  // Обработчик авторизации платежа
  const handleAuthorizePayment = async (payment: any) => {
    try {
      setIsAuthorizingPayment(true);
      
      toast.info('Подготовка авторизации карты...', {
        description: 'Пожалуйста, подождите',
        duration: 3000
      });
      
      // ВАЖНО: Используем единый формат URL для редиректа
      // Упрощаем URL и передаем только courier_id, внешняя система сама обработает платеж
      const returnUrl = `${window.location.origin}/payment/auth/complete?courier_id=${payment.courierId}`;
      
      // Формируем URL для авторизационного платежа
      const authUrl = `https://yoomoney.ru/checkout/payments/v2/contract?orderId=${payment.id}&returnUrl=${encodeURIComponent(returnUrl)}`;
      
      // Показываем уведомление о том, что авторизация открывается в новом окне
      toast.info('Авторизация карты открывается в новом окне', {
        description: 'Пожалуйста, разрешите всплывающие окна в браузере',
        duration: 5000
      });
      
      const authWindow = window.open(authUrl, '_blank');
      
      if (!authWindow) {
        toast.error('Не удалось открыть окно авторизации', {
          description: 'Проверьте настройки блокировки всплывающих окон в браузере'
        });
        setIsAuthorizingPayment(false);
        return;
      }
      
      // Добавляем задержку перед обновлением контекста
      // Это позволит пользователю завершить процесс авторизации
      setTimeout(async () => {
        await refreshAuthPayment();
        setIsAuthorizingPayment(false);
      }, 3000);
      
    } catch (error) {
      console.error('Ошибка при авторизации карты:', error);
      toast.error('Произошла ошибка при подготовке авторизации карты', {
        description: 'Пожалуйста, попробуйте позже'
      });
      setIsAuthorizingPayment(false);
    }
  };
  
  // Если включен обход проверки, показываем контент сразу
  if (bypassCheck) {
    console.log('AuthPaymentBlocker: Обход проверки включен, показываем контент');
    return <>{children}</>;
  }
  
  // Если загрузка данных еще не завершена и не прошел timeout, показываем индикатор загрузки
  if (isAuthPaymentLoading && !forceShowContent) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
          <p className="text-sm text-muted-foreground">Проверка статуса платежей...</p>
        </div>
      </div>
    );
  }
  
  // Если есть авторизационный платеж, показываем блокирующий экран
  if (authPayment && !forceShowContent) {
    return (
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
          <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-2">
            Доступ к функциям ограничен
          </h2>
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">
            Для полного доступа к личному кабинету необходимо авторизовать вашу карту.
            Пожалуйста, завершите процесс авторизации карты, чтобы продолжить работу с сервисом.
          </p>
        </div>
        
        <AuthPaymentCard 
          authPayment={authPayment}
          onAuthorize={handleAuthorizePayment}
          loading={isAuthorizingPayment}
        />
      </div>
    );
  }
  
  // Если нет авторизационных платежей или сработал принудительный показ, показываем основной контент
  console.log('AuthPaymentBlocker: Показываем основной контент');
  return <>{children}</>;
};
